import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../../Utils/axiosInstance";
import {
  DEFAULT_MONTH,
  DEFAULT_YEAR,
  defaultSummaryCards,
  defaultDonutSections,
  defaultTrendSeries,
  buildDashboardData,
  formatDashboardTitle
} from "./utils";
import { getTrendChartOption } from "./chartConfigs";

export const useEDADashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentMonth, setCurrentMonth] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_MONTH);
  const [months, setMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
  const [dashboardData, setDashboardData] = useState({
    summaryCards: defaultSummaryCards,
    donutSections: defaultDonutSections,
    trendSeries: defaultTrendSeries,
    excelFiles: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { dashboard, selectedProcess } = location.state || {};
  const dashboardName = dashboard?.eda_name;
  const title = formatDashboardTitle(dashboardName);
  const selectedMonthLabel =
    months.find((month) => month.value === selectedMonth)?.label || "";

  const consolidatedReport = useMemo(
    () =>
      dashboardData.excelFiles.find((file) =>
        file.id.toLowerCase().includes("consolidated")
      ),
    [dashboardData.excelFiles]
  );

  const trendChartOption = useMemo(
    () => getTrendChartOption(dashboardData.trendSeries),
    [dashboardData.trendSeries]
  );

  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const response = await axiosInstance.get("/eda/fixed-cost-month-list");

        if (response?.data?.status) {
          setMonths(response.data.items);

          const currentMonthObj = response.data.items.find((item) =>
            item.label.startsWith("Current Month")
          );

          if (currentMonthObj) {
            setCurrentMonth(currentMonthObj.value);
            setSelectedMonth(currentMonthObj.value);
          }
        }
      } catch (error) {
        console.error("Failed to fetch month list", error);
      }
    };

    fetchMonths();
  }, []);

  useEffect(() => {
    if (!location.state) {
      return;
    }

    let ignore = false;

    const fetchDashboardData = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axiosInstance.post("/eda/eda1data", {
          Month: selectedMonth,
          Year: selectedYear,
        });

        if (!ignore && response?.data?.status && response?.data?.items) {
          setDashboardData(
            buildDashboardData(response.data.items, selectedMonth, selectedYear)
          );
        }
      } catch (apiError) {
        if (!ignore) {
          setError(
            apiError?.response?.data?.message || "Failed to fetch EDA data."
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      ignore = true;
    };
  }, [location.state, selectedMonth, selectedYear]);

  return {
    navigate,
    location,
    currentMonth,
    selectedMonth,
    setSelectedMonth,
    months,
    selectedYear,
    setSelectedYear,
    dashboardData,
    loading,
    error,
    dashboard,
    selectedProcess,
    title,
    selectedMonthLabel,
    consolidatedReport,
    trendChartOption,
  };
};

